import * as React from 'react';
import {Trans} from '@deviceinsight/ng-ui-scale-lib/api';
import classNames from 'classnames';
import type {AssetGroup} from '@deviceinsight/ng-ui-api-client';
import {Button, Icon} from '@deviceinsight/ng-ui-basic-components';
import {UiCondition} from '../../../modules/ruleengine/types';
import {createCondition} from '../ruleEditHelpers';
import RuleConditionEdit from './RuleConditionEdit';
import {OPERATOR_OR} from '../../../modules/ruleengine/config/consts';
import DeleteButton from '../../../components/common/DeleteButton';
type Props = {
	condition: UiCondition;
	onChange: (updatedCondition: UiCondition, index: number) => void;
	index: number;
	onRemove: (index: number) => void;
	isTrigger: boolean;
	assetGroup: AssetGroup;
	isAddable: boolean;
	canEdit: boolean;
};
export default class RuleComplexConditionEdit extends React.PureComponent<Props> {
	addCondition = () => {
		const {
			condition,
			condition: {nestedConditions},
			onChange,
			index
		} = this.props;

		if (nestedConditions) {
			const updatedCondition = {
				...condition,
				compareOperator: undefined,
				assetUrn: undefined,
				nestedConditions: [...nestedConditions, createCondition(undefined)],
				metadata: {...condition.metadata, isValid: false}
			};
			onChange(updatedCondition, index);
		}
	};
	removeNestedCondition = (nestedIndex: number) => {
		const {
			condition,
			condition: {nestedConditions},
			onChange,
			index
		} = this.props;

		if (nestedConditions) {
			const updatedConditions = [...nestedConditions];
			updatedConditions.splice(nestedIndex, 1);

			if (updatedConditions.length == 1) {
				// make a simple condition from this since complex with only one child are not meaningful...
				const updatedCondition = updatedConditions[0];
				onChange(updatedCondition, index);
			} else {
				const invalidCondition = updatedConditions.find((condition) => !condition.metadata.isValid);
				const updatedCondition = {
					...condition,
					nestedConditions: updatedConditions,
					metadata: {...condition.metadata, isValid: invalidCondition ? false : true}
				};
				onChange(updatedCondition, index);
			}
		}
	};
	updateNestedCondition = (nestedCondition: UiCondition, nestedIndex: number) => {
		const {
			condition,
			condition: {nestedConditions},
			onChange,
			index
		} = this.props;

		if (nestedConditions) {
			const updatedConditions = nestedConditions.map((condition, index) =>
				nestedIndex === index ? nestedCondition : condition
			);
			const invalidChild = updatedConditions.find((updatedCondition) => !updatedCondition.metadata.isValid);
			onChange(
				{
					...condition,
					nestedConditions: updatedConditions,
					metadata: {...condition.metadata, isValid: invalidChild ? false : true}
				},
				index
			);
		}
	};

	render() {
		const {
			condition: {nestedConditions, logicOperator},
			index,
			onRemove,
			isTrigger,
			assetGroup,
			isAddable,
			canEdit
		} = this.props;
		const complexRuleClass = classNames({
			complex_rule_items: true,
			or: logicOperator === OPERATOR_OR
		});
		const operatorClass = classNames({
			logic_operator: true,
			or: logicOperator === OPERATOR_OR
		});
		return (
			<div className="complex_rule_edit">
				<div className="complex_rule_edit_line">
					<div className={complexRuleClass}>
						{nestedConditions &&
							nestedConditions.map((nestedCondition, index) => {
								if (nestedCondition.nestedConditions && nestedCondition.nestedConditions.length) {
									return (
										<RuleComplexConditionEdit
											assetGroup={assetGroup}
											condition={nestedCondition}
											onChange={this.updateNestedCondition}
											index={index}
											canEdit={canEdit}
											key={nestedCondition.metadata.uuid}
											onRemove={this.removeNestedCondition}
											isTrigger={isTrigger}
											isAddable={isAddable}
										/>
									);
								} else {
									return (
										<RuleConditionEdit
											assetGroup={assetGroup}
											condition={nestedCondition}
											onChange={this.updateNestedCondition}
											index={index}
											canEdit={canEdit}
											key={nestedCondition.metadata.uuid}
											onRemove={this.removeNestedCondition}
											isTrigger={isTrigger}
											isChild={isTrigger}
											isAddable={isAddable}
										/>
									);
								}
							})}
					</div>
					<div className="rule-functions">
						<div className={operatorClass}>
							<Trans i18nKey={`ruleengine.${logicOperator || 'unknown'}`}>
								{logicOperator === OPERATOR_OR ? 'OR' : 'AND'}
							</Trans>
						</div>
						{canEdit && (
							<DeleteButton
								data-testid="remove-nested-condition-button"
								onClick={() => onRemove(index)}
							/>
						)}
						{isAddable && canEdit && (
							<Button naked onClick={() => this.addCondition()}>
								<Icon add />
							</Button>
						)}
					</div>
				</div>
			</div>
		);
	}
}
